import Cookies from 'js-cookie';
import { camelCase, isArray, isObject, snakeCase, transform } from 'lodash';
import { sessionDataCookieName } from '../context/Session';

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL ?? process.env.REACT_APP_BASE_URL;

const CSRF_COOKIE_NAME = process.env.REACT_APP_CSRF_COOKIE_NAME ?? 'csrftoken';

const changeCasing = (
  obj: Record<string, unknown>,
  modifier: (string: string) => string
) =>
  transform(
    obj,
    (result: Record<string, unknown>, value: unknown, key: string, target) => {
      const camelKey = isArray(target) ? key : modifier(key);
      result[camelKey] = isObject(value)
        ? changeCasing(value as Record<string, unknown>, modifier)
        : value;
    }
  );

export const toCamelCase = (obj: Record<string, unknown>) =>
  changeCasing(obj, camelCase);
export const toSnakeCase = (obj: Record<string, unknown>) =>
  changeCasing(obj, snakeCase);

export interface ErrorResponse {
  errorCode: string;
  errorMessage?: string;
  errorDetails?: Record<string, string[]>;
  reason?: string;
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: Record<string, unknown> | FormData;
  returnRawResponse?: boolean;
  stream?: boolean;
};

/**
 * A wrapper function for making API calls
 * Can be use to modify the fetch requests, modify headers etc etc globally
 * We can add a second optional argument options of type object to achieve the same
 *
 * In the case of an error the BE will response it will either:
 * - ErrorResponse as above. Then it's a "known" error in the BE.
 * - Not Json, empty json or a json with {details}.
 *
 * For the latter case it's a more internal error and we don't want to expose
 * that to the end user.
 *
 * We always have snake_case in the BE and camelCase in the FE. Hence we need to
 * change the casing.
 */
export async function fetchAPI(
  url: string,
  options: RequestOptions & { stream: true }
): Promise<Uint8Array[]>;
export async function fetchAPI<T>(
  url: string,
  options?: RequestOptions
): Promise<T>;
export async function fetchAPI<T>(
  url: string,
  options?: RequestOptions
): Promise<T> {
  let data: Record<string, unknown> | null = null;
  try {
    const fullBaseDomain = BASE_URL ? BASE_URL + '/api/bo/' : '/api/bo/';
    const actualBody =
      options?.body instanceof FormData
        ? options.body
        : options?.body
          ? JSON.stringify(changeCasing(options.body, snakeCase))
          : undefined;
    const response = await fetch(`${fullBaseDomain}${url}`, {
      ...options,
      body: actualBody,
      headers: {
        // eslint-disable-next-line @typescript-eslint/no-misused-spread
        ...options?.headers,
        'x-csrftoken': Cookies.get(CSRF_COOKIE_NAME) ?? '',
        ...(options?.body instanceof FormData
          ? {}
          : { 'Content-Type': 'application/json' }),
      },
      credentials: 'include',
    });

    if (response.status === 401) {
      Cookies.remove(sessionDataCookieName);
      window.location.replace(
        window.location.pathname.startsWith('/neo-office/')
          ? '/neo-office/index.html#/login'
          : '/login'
      );
    }

    if (options?.returnRawResponse && response.ok) {
      return response as T;
    }

    if (options?.stream) {
      if (!response.body) {
        throw new Error('ReadableStream not supported in this browser.');
      }

      const reader = response.body.getReader();
      const chunks: Uint8Array[] = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
      }
      return chunks as unknown as T;
    }

    if (response.status === 204) {
      return null as unknown as T;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    data = changeCasing(await response.json(), camelCase);

    if (response.ok) {
      return data as T;
    }

    if (!('errorCode' in data)) throw Error('Generic error');
  } catch (exception) {
    console.error(exception);
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw {
      errorCode: 'INTERNAL_SERVER_ERROR',
      errorMessage: 'A server error occurred.',
      errorDetails: { error: ['A server error occurred.'] },
    } as ErrorResponse;
  }
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw data as unknown as ErrorResponse;
}
